import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { AdminLayout } from '../components/AdminSidebar';
import { api, formatApiError } from '../lib/api';
import { plaqueStatusLabel } from '../lib/plaqueStatus';
import { toast } from 'sonner';

function money(usd) {
  if (usd === null || usd === undefined) return '$0.00';
  return `$${Number(usd).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}
function shortDate(iso) { return iso ? iso.slice(0, 10) : '—'; }
function shortDT(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const TABS = [
  { id: 'overview',   label: 'Overview' },
  { id: 'tree',       label: 'Family tree' },
  { id: 'media',      label: 'Media' },
  { id: 'plaque',     label: 'Plaque & QR' },
  { id: 'orders',     label: 'Orders & billing' },
  { id: 'admins',     label: 'Admins' },
  { id: 'tributes',   label: 'Tributes' },
];

export default function AdminMemorialDetail() {
  const { memorialId } = useParams();
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]     = useState('overview');
  const [bannerHidden, setBannerHidden] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/memorials/${memorialId}/dossier`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [memorialId]);

  useEffect(() => { reload(); }, [reload]);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <AdminLayout>
          <div className="text-[hsl(var(--primary)/0.6)]" data-testid="admin-memorial-loading">
            {loading ? 'Loading dossier…' : 'Memorial not found.'}
          </div>
        </AdminLayout>
      </div>
    );
  }

  const m = data.memorial || {};
  const owner = data.owner;
  const slug = m.slug;
  const publicUrl = data.qr?.public_url;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <AdminLayout>
        <AdminViewBanner
          hidden={bannerHidden}
          onToggle={() => setBannerHidden((v) => !v)}
          ownerEmail={owner?.email}
        />

        <header className="mb-6 flex items-start justify-between gap-4 flex-wrap" data-testid="admin-memorial-header">
          <div>
            <Link
              to={owner ? `/admin/accounts/${owner.user_id}` : '/admin/accounts'}
              className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))] hover:underline"
              data-testid="admin-memorial-back"
            >
              ← {owner ? `Back to ${owner.name || owner.email}` : 'Back to accounts'}
            </Link>
            <h1 className="font-serif text-4xl text-[hsl(var(--primary))] mt-2" data-testid="admin-memorial-title">
              {m.full_name || '(no name)'}
            </h1>
            <div className="text-sm text-[hsl(var(--primary)/0.7)] mt-1">
              {publicUrl ? (
                <a href={publicUrl} target="_blank" rel="noreferrer" className="hover:underline" data-testid="admin-memorial-public-url">
                  {publicUrl}
                </a>
              ) : <span className="font-mono">{slug}</span>}
              {m.is_demo && <span className="ml-2 px-2 py-0.5 rounded-full bg-[hsl(var(--accent)/0.18)] text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--primary))]">Demo</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="px-3 py-1 rounded-full border border-[hsl(var(--primary)/0.18)] uppercase tracking-[0.16em]" data-testid="admin-memorial-plan-pill">
              {m.plan_status || 'active'}
            </span>
            <span className="px-3 py-1 rounded-full border border-[hsl(var(--primary)/0.18)] uppercase tracking-[0.16em]" data-testid="admin-memorial-qr-pill">
              QR · {data.qr?.access_mode || 'public'}
            </span>
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm('Regenerate this memorial\'s QR against the production URL? This refreshes cached artwork links on all of its plaque orders.')) return;
                try {
                  const { data: r } = await api.post(`/admin/memorials/${memorialId}/regenerate-qr`);
                  toast.success(`QR regenerated → ${r.qr_target_url} (${r.orders_updated} order${r.orders_updated === 1 ? '' : 's'} refreshed)`);
                  reload();
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              }}
              className="px-3 py-1 rounded-full border border-[hsl(var(--accent))] text-[hsl(var(--primary))] uppercase tracking-[0.16em] hover:bg-[hsl(var(--accent)/0.12)] transition font-medium"
              data-testid="admin-memorial-regenerate-qr"
              title="Regenerate the QR code and refresh plaque fulfillment artwork with the canonical production URL"
            >
              Regenerate QR Code
            </button>
            <Link
              to={`/dashboard/m/${memorialId}?support=1`}
              className="px-3 py-1 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--accent))] uppercase tracking-[0.16em] hover:opacity-90 transition font-medium"
              data-testid="admin-memorial-open-as-owner"
              title="Open this memorial's dashboard exactly as the account owner sees it (Admin Support Mode — every action is audit-logged)"
            >
              View as Account Owner →
            </Link>
          </div>
        </header>

        <Tabs tab={tab} onChange={setTab} />

        {tab === 'overview'   && <Overview memorial={m} owner={owner} memorialId={memorialId} onReload={reload} counts={{
          media: data.media_counts?.total || 0,
          tributes: data.tribute_count || 0,
          family: data.family_members?.length || 0,
          admins: data.admins?.length || 0,
        }} />}
        {tab === 'tree'       && <FamilyTreeTab members={data.family_members} />}
        {tab === 'media'      && <MediaTab media={data.media} counts={data.media_counts} lifeEvents={data.life_events} />}
        {tab === 'plaque'     && <PlaqueQRTab memorial={m} qr={data.qr} memorialId={memorialId} onReload={reload} />}
        {tab === 'orders'     && <OrdersBillingTab
                                  plaqueOrders={data.plaque_orders}
                                  qrOrders={data.qr_orders}
                                  engravingRequests={data.engraving_requests}
                                  payments={data.payments}
                                  onReload={reload}
                                />}
        {tab === 'admins'     && <AdminsTab owner={owner} admins={data.admins} invites={data.admin_invites} />}
        {tab === 'tributes'   && <TributesTab tributes={data.tributes} total={data.tribute_count} />}
      </AdminLayout>
    </div>
  );
}

// =============================================================================
// Admin View banner
// =============================================================================
function AdminViewBanner({ hidden, onToggle, ownerEmail }) {
  if (hidden) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className="mb-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-[11px] uppercase tracking-[0.16em] border border-amber-300 hover:bg-amber-200 transition"
        data-testid="admin-memorial-banner-restore"
      >
        <span className="w-2 h-2 rounded-full bg-amber-600 animate-pulse" />
        Admin view
      </button>
    );
  }
  return (
    <div
      className="mb-6 rounded-xl border-2 border-amber-400 bg-amber-50 px-5 py-4 flex items-start justify-between gap-4"
      data-testid="admin-memorial-banner"
    >
      <div>
        <div className="flex items-center gap-2 text-amber-900 font-medium text-sm uppercase tracking-[0.18em]">
          <span className="w-2 h-2 rounded-full bg-amber-600 animate-pulse" />
          Admin view — full edit access
        </div>
        <p className="mt-2 text-amber-900/85 text-sm max-w-3xl leading-relaxed">
          You are viewing this memorial as a platform administrator. Edit and fulfillment actions are available throughout the page.
          {ownerEmail && <> The account owner is <span className="font-mono">{ownerEmail}</span>.</>} Every change is recorded in the audit log with a full before/after diff.
        </p>
      </div>
      <button
        type="button"
        onClick={onToggle}
        className="text-amber-900 hover:text-amber-700 text-xs uppercase tracking-[0.16em] flex-none"
        data-testid="admin-memorial-banner-hide"
      >
        Minimize
      </button>
    </div>
  );
}

// =============================================================================
// Tabs
// =============================================================================
function Tabs({ tab, onChange }) {
  return (
    <div className="mb-6 border-b border-[hsl(var(--primary)/0.12)] overflow-x-auto" data-testid="admin-memorial-tabs">
      <div className="flex gap-1 min-w-max">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={
              'px-4 py-2.5 text-sm font-medium transition border-b-2 ' +
              (tab === t.id
                ? 'text-[hsl(var(--primary))] border-[hsl(var(--accent))]'
                : 'text-[hsl(var(--primary)/0.55)] border-transparent hover:text-[hsl(var(--primary))]')
            }
            data-testid={`admin-memorial-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Overview
// =============================================================================
function Overview({ memorial: m, owner, memorialId, onReload, counts }) {
  const [editOpen, setEditOpen] = useState(false);
  const [pinResult, setPinResult] = useState(null);

  const handleResetPin = async () => {
    if (!window.confirm('Generate a fresh 4-digit QR PIN? The current PIN will be invalidated immediately.')) return;
    try {
      const { data } = await api.post(`/admin/memorials/${memorialId}/reset-qr-pin`);
      setPinResult(data.new_pin);
      // Intentionally NOT calling onReload() here: re-fetching the dossier
      // would unmount Overview and wipe `pinResult` before the admin can
      // read the rotated PIN. No rendered field on this tab depends on
      // the qr_pin, so skipping the reload is safe.
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-memorial-tab-overview-content">
      <Section title="Counts">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Family members" value={counts.family} testid="admin-memorial-stat-family" />
          <Stat label="Admins"          value={counts.admins} testid="admin-memorial-stat-admins" />
          <Stat label="Media items"     value={counts.media}  testid="admin-memorial-stat-media" />
          <Stat label="Tributes"        value={counts.tributes} testid="admin-memorial-stat-tributes" />
        </div>
      </Section>

      <SectionWithAction
        title="Memorial profile"
        testid="admin-memorial-profile"
        action={(
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="text-xs uppercase tracking-[0.16em] text-[hsl(var(--accent))] hover:underline"
            data-testid="admin-memorial-edit-profile-btn"
          >
            Edit ✎
          </button>
        )}
      >
        <Grid>
          <KV label="Memorial ID"     value={m.memorial_id} mono />
          <KV label="Slug"            value={m.slug} mono />
          <KV label="Full name"       value={m.full_name} />
          <KV label="Hometown"        value={m.hometown} />
          <KV label="Birthplace"      value={m.birthplace} />
          <KV label="Date of birth"   value={shortDate(m.date_of_birth)} />
          <KV label="Date of passing" value={shortDate(m.date_of_passing)} />
          <KV label="Occupation"      value={m.occupation} />
          <KV label="Family role"     value={m.family_role} />
          <KV label="Hero tagline"    value={m.hero_tagline} />
          <KV label="Plan status"     value={m.plan_status || 'active'} />
          <KV label="Published"       value={m.published === false ? 'No' : 'Yes'} />
          <KV label="AI enabled"      value={m.ai_enabled ? 'Yes' : 'No'} />
          <KV label="Created"         value={shortDate(m.created_at)} />
          <KV label="Scan count"      value={m.scan_count ?? 0} />
        </Grid>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleResetPin}
            className="text-xs uppercase tracking-[0.16em] text-[hsl(var(--accent))] hover:underline"
            data-testid="admin-memorial-reset-pin-btn"
          >
            Reset QR PIN
          </button>
          {pinResult && (
            <span className="text-xs px-3 py-1 rounded-full bg-amber-50 border border-amber-300 text-amber-900 font-mono" data-testid="admin-memorial-new-pin">
              New PIN: {pinResult} (share with family once)
            </span>
          )}
        </div>
      </SectionWithAction>

      <Section title="Cemetery / resting place" testid="admin-memorial-cemetery">
        <Grid>
          <KV label="Cemetery name"   value={m.cemetery_name} />
          <KV label="Address"         value={m.cemetery_address} />
          <KV label="City"            value={m.cemetery_city} />
          <KV label="Region / state"  value={m.cemetery_region} />
          <KV label="Country"         value={m.cemetery_country} />
          <KV label="Section"         value={m.cemetery_section} />
          <KV label="Lot"             value={m.cemetery_lot} />
          <KV label="Row"             value={m.gravesite_row} />
          <KV label="Plot"            value={m.gravesite_plot} />
          <KV label="GPS lat"         value={m.gravesite_lat} mono />
          <KV label="GPS lng"         value={m.gravesite_lng} mono />
          <KV label="Visibility"      value={m.gravesite_visibility} />
        </Grid>
      </Section>

      <Section title="Funeral home" testid="admin-memorial-funeral">
        <Grid>
          <KV label="Enabled"   value={m.funeral_home_enabled ? 'Yes' : 'No'} />
          <KV label="Name"      value={m.funeral_home_name} />
          <KV label="Phone"     value={m.funeral_home_phone} />
          <KV label="Website"   value={m.funeral_home_website} />
          <KV label="Address"   value={m.funeral_home_address} />
        </Grid>
      </Section>

      {owner && (
        <Section title="Owner account" testid="admin-memorial-owner">
          <Grid>
            <KV label="Name"       value={owner.name || `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || '—'} />
            <KV label="Email"      value={owner.email} />
            <KV label="Phone"      value={owner.phone} />
            <KV label="Plan"       value={owner.plan} />
            <KV label="Country"    value={owner.country} />
            <KV label="Created"    value={shortDate(owner.created_at)} />
            <KV label="Last login" value={shortDate(owner.last_login_at)} />
          </Grid>
          <div className="mt-3">
            <Link
              to={`/admin/accounts/${owner.user_id}`}
              className="text-xs uppercase tracking-[0.16em] text-[hsl(var(--accent))] hover:underline"
              data-testid="admin-memorial-open-owner"
            >
              Open full account →
            </Link>
          </div>
        </Section>
      )}

      {editOpen && (
        <EditMemorialModal
          memorial={m}
          memorialId={memorialId}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); onReload?.(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-[hsl(var(--primary)/0.12)] bg-[hsl(var(--card))] p-4" data-testid={testid}>
      <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)]">{label}</div>
      <div className="font-serif text-3xl text-[hsl(var(--primary))] mt-1">{value}</div>
    </div>
  );
}

// =============================================================================
// Family tree tab
// =============================================================================
function FamilyTreeTab({ members }) {
  if (!members || members.length === 0) {
    return <div data-testid="admin-memorial-tab-tree-content"><Empty>No family members added yet.</Empty></div>;
  }
  return (
    <div data-testid="admin-memorial-tab-tree-content">
      <Section title={`Family members (${members.length})`} testid="admin-memorial-tree">
        <Table headers={['Name', 'Relationship', 'Birth', 'Death', 'Location', 'Linked memorial']}>
          {members.map((f) => (
            <tr key={f.family_id} className="border-t border-[hsl(var(--primary)/0.08)]">
              <td className="py-2 px-3">{f.name}</td>
              <td className="py-2 px-3">{f.relationship}</td>
              <td className="py-2 px-3">{shortDate(f.birth_date)}</td>
              <td className="py-2 px-3">{f.is_living === false ? shortDate(f.death_date) : 'Living'}</td>
              <td className="py-2 px-3">{f.location || '—'}</td>
              <td className="py-2 px-3 font-mono text-[10px]">
                {f.linked_memorial_id
                  ? <Link to={`/admin/memorials/${f.linked_memorial_id}`} className="text-[hsl(var(--accent))] hover:underline">{f.linked_memorial_id}</Link>
                  : '—'}
              </td>
            </tr>
          ))}
        </Table>
      </Section>
    </div>
  );
}

// =============================================================================
// Media tab
// =============================================================================
function MediaTab({ media, counts, lifeEvents }) {
  const photos = (media || []).filter((x) => (x.kind || x.type || '').startsWith('image'));
  const videos = (media || []).filter((x) => (x.kind || x.type || '').startsWith('video'));
  return (
    <div data-testid="admin-memorial-tab-media-content">
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Stat label="Total media" value={counts?.total || 0} testid="admin-memorial-media-total" />
        <Stat label="Photos"      value={counts?.photos || photos.length} testid="admin-memorial-media-photos" />
        <Stat label="Videos"      value={counts?.videos || videos.length} testid="admin-memorial-media-videos" />
      </div>

      {(media && media.length > 0) ? (
        <Section title="Recent media">
          <Table headers={['Type', 'Caption', 'Uploaded by', 'Visibility', 'Created']}>
            {media.slice(0, 50).map((it) => (
              <tr key={it.media_id || `${it.memorial_id}-${it.created_at}`} className="border-t border-[hsl(var(--primary)/0.08)]">
                <td className="py-2 px-3 text-xs">{it.kind || it.type || '—'}</td>
                <td className="py-2 px-3">{it.caption || it.title || '—'}</td>
                <td className="py-2 px-3 font-mono text-[10px]">{it.uploaded_by || '—'}</td>
                <td className="py-2 px-3">{it.visibility || it.access || 'public'}</td>
                <td className="py-2 px-3">{shortDate(it.created_at)}</td>
              </tr>
            ))}
          </Table>
        </Section>
      ) : (
        <Empty>No photos or videos uploaded yet.</Empty>
      )}

      <Section title={`Life events (${lifeEvents?.length || 0})`} testid="admin-memorial-life-events">
        {(!lifeEvents || lifeEvents.length === 0) ? <Empty>No life events.</Empty> : (
          <Table headers={['Date', 'Title', 'Location']}>
            {lifeEvents.map((ev) => (
              <tr key={ev.event_id} className="border-t border-[hsl(var(--primary)/0.08)]">
                <td className="py-2 px-3">{shortDate(ev.event_date)}</td>
                <td className="py-2 px-3">{ev.title}</td>
                <td className="py-2 px-3">{ev.location || '—'}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </div>
  );
}

// =============================================================================
// Plaque & QR tab
// =============================================================================
function PlaqueQRTab({ memorial, qr, memorialId }) {
  const [qrBlobUrl, setQrBlobUrl] = useState(null);
  const [qrLoading, setQrLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      setQrLoading(true);
      try {
        const { data } = await api.get(`/admin/memorials/${memorialId}/qr.png`, {
          responseType: 'blob',
        });
        if (!cancelled) {
          createdUrl = URL.createObjectURL(data);
          setQrBlobUrl(createdUrl);
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setQrLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [memorialId]);

  const handleDownload = async () => {
    try {
      const { data } = await api.get(`/admin/memorials/${memorialId}/qr.png`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-${memorial?.slug || memorialId}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('QR PNG downloaded (audit logged).');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-memorial-tab-plaque-content">
      <Section title="QR code">
        <div className="rounded-2xl border border-[hsl(var(--primary)/0.12)] bg-[hsl(var(--card))] p-6 flex flex-col sm:flex-row gap-6 items-start">
          {qrBlobUrl ? (
            <img
              src={qrBlobUrl}
              alt={`QR for ${memorial?.full_name}`}
              className="w-48 h-48 rounded-md border border-[hsl(var(--primary)/0.18)] bg-white p-2"
              data-testid="admin-memorial-qr-img"
            />
          ) : (
            <div
              className="w-48 h-48 rounded-md border border-[hsl(var(--primary)/0.18)] bg-white flex items-center justify-center text-xs text-[hsl(var(--primary)/0.55)]"
              data-testid="admin-memorial-qr-img-loading"
            >
              {qrLoading ? 'Loading QR…' : 'QR unavailable'}
            </div>
          )}
          <div className="flex-1 space-y-3">
            <KV label="Public URL"  value={qr?.public_url || '—'} />
            <KV label="Access mode" value={qr?.access_mode || 'public'} />
            <KV label="PIN set"     value={qr?.pin_set ? `Yes (${qr?.pin_masked})` : 'No'} />
            <KV label="Plan status" value={qr?.plan_status || 'active'} />
            <button
              type="button"
              onClick={handleDownload}
              className="mt-3 px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--accent))] text-sm font-medium hover:opacity-90 transition"
              data-testid="admin-memorial-qr-download-btn"
            >
              Download QR PNG (for fulfillment)
            </button>
            <p className="text-xs text-[hsl(var(--primary)/0.55)]">
              Used for acrylic / stainless plaque engraving. Download is audit-logged.
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
}

// =============================================================================
// Orders & Billing tab
// =============================================================================
function OrdersBillingTab({ plaqueOrders, qrOrders, engravingRequests, payments, onReload }) {
  const [editing, setEditing] = useState(null); // { kind, row }

  return (
    <div data-testid="admin-memorial-tab-orders-content">
      <Section title={`Plaque orders (${plaqueOrders?.length || 0})`} testid="admin-memorial-plaque-orders">
        {(!plaqueOrders || plaqueOrders.length === 0) ? <Empty>No plaque orders.</Empty> : (
          <Table headers={['Order', 'Product', 'Material', 'Qty', 'Amount', 'Status', 'Tracking', 'Shipped', '']}>
            {plaqueOrders.map((o) => (
              <tr key={o.order_id} className="border-t border-[hsl(var(--primary)/0.08)]">
                <td className="py-2 px-3 font-mono text-xs">{(o.order_id || '').replace('plaque_', '')}</td>
                <td className="py-2 px-3">{o.product_label || o.product_id || '—'}</td>
                <td className="py-2 px-3">{o.material || o.product_material || '—'}</td>
                <td className="py-2 px-3">{o.quantity}</td>
                <td className="py-2 px-3">{money(o.amount_usd)}</td>
                <td className="py-2 px-3">{plaqueStatusLabel(o.status) || '—'}</td>
                <td className="py-2 px-3 font-mono text-[10px]">
                  {o.tracking_number ? `${o.carrier || ''} ${o.tracking_number}`.trim() : '—'}
                </td>
                <td className="py-2 px-3">{shortDate(o.shipped_at)}</td>
                <td className="py-2 px-3 text-right">
                  <button
                    type="button"
                    onClick={() => setEditing({ kind: 'plaque', row: o })}
                    className="text-[hsl(var(--accent))] hover:underline text-xs uppercase tracking-[0.16em]"
                    data-testid={`admin-memorial-edit-plaque-${o.order_id}`}
                  >
                    Edit ✎
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title={`QR orders (${qrOrders?.length || 0})`} testid="admin-memorial-qr-orders">
        {(!qrOrders || qrOrders.length === 0) ? <Empty>No QR orders.</Empty> : (
          <Table headers={['Order', 'Type', 'Qty', 'Status', 'Tracking', 'Date', '']}>
            {qrOrders.map((o) => (
              <tr key={o.order_id} className="border-t border-[hsl(var(--primary)/0.08)]">
                <td className="py-2 px-3 font-mono text-xs">{(o.order_id || '').replace('ord_', '')}</td>
                <td className="py-2 px-3">{o.product_label || o.order_type}</td>
                <td className="py-2 px-3">{o.quantity}</td>
                <td className="py-2 px-3">{o.status || '—'}</td>
                <td className="py-2 px-3 font-mono text-[10px]">
                  {o.tracking_number ? `${o.carrier || ''} ${o.tracking_number}`.trim() : '—'}
                </td>
                <td className="py-2 px-3">{shortDate(o.created_at)}</td>
                <td className="py-2 px-3 text-right">
                  <button
                    type="button"
                    onClick={() => setEditing({ kind: 'qr', row: o })}
                    className="text-[hsl(var(--accent))] hover:underline text-xs uppercase tracking-[0.16em]"
                    data-testid={`admin-memorial-edit-qr-${o.order_id}`}
                  >
                    Edit ✎
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title={`Engraving quotes (${engravingRequests?.length || 0})`} testid="admin-memorial-engraving">
        {(!engravingRequests || engravingRequests.length === 0) ? <Empty>No engraving quote requests.</Empty> : (
          <Table headers={['Request', 'Deceased', 'Cemetery', 'Status', 'Quote', 'Created', '']}>
            {engravingRequests.map((r) => (
              <tr key={r.request_id} className="border-t border-[hsl(var(--primary)/0.08)]">
                <td className="py-2 px-3 font-mono text-xs">{(r.request_id || '').replace('engreq_', '')}</td>
                <td className="py-2 px-3">{r.deceased_full_name}</td>
                <td className="py-2 px-3">{r.cemetery_name}</td>
                <td className="py-2 px-3">{r.status || '—'}</td>
                <td className="py-2 px-3">{r.quote_amount != null ? money(r.quote_amount) : '—'}</td>
                <td className="py-2 px-3">{shortDate(r.created_at)}</td>
                <td className="py-2 px-3 text-right">
                  <button
                    type="button"
                    onClick={() => setEditing({ kind: 'engraving', row: r })}
                    className="text-[hsl(var(--accent))] hover:underline text-xs uppercase tracking-[0.16em]"
                    data-testid={`admin-memorial-edit-engraving-${r.request_id}`}
                  >
                    Edit ✎
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title={`Payments (${payments?.length || 0})`} testid="admin-memorial-payments">
        {(!payments || payments.length === 0) ? <Empty>No payments tied to this memorial.</Empty> : (
          <Table headers={['Plan', 'Cycle', 'Amount', 'Status', 'Date']}>
            {payments.map((p) => (
              <tr key={p.transaction_id} className="border-t border-[hsl(var(--primary)/0.08)]">
                <td className="py-2 px-3">{p.plan_id || '—'}</td>
                <td className="py-2 px-3">{p.billing_cycle || '—'}</td>
                <td className="py-2 px-3">{money(p.amount)}</td>
                <td className="py-2 px-3">{p.payment_status}</td>
                <td className="py-2 px-3">{shortDate(p.created_at)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      {editing && (
        <EditOrderModal
          kind={editing.kind}
          row={editing.row}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onReload?.(); }}
        />
      )}
    </div>
  );
}

// =============================================================================
// Admins tab
// =============================================================================
function AdminsTab({ owner, admins, invites }) {
  return (
    <div data-testid="admin-memorial-tab-admins-content">
      {owner && (
        <Section title="Owner">
          <Grid>
            <KV label="Name"  value={owner.name} />
            <KV label="Email" value={owner.email} />
            <KV label="Phone" value={owner.phone} />
            <KV label="User ID" value={owner.user_id} mono />
          </Grid>
          <div className="mt-3">
            <Link
              to={`/admin/accounts/${owner.user_id}`}
              className="text-xs uppercase tracking-[0.16em] text-[hsl(var(--accent))] hover:underline"
              data-testid="admin-memorial-owner-link"
            >
              Open owner account →
            </Link>
          </div>
        </Section>
      )}

      <Section title={`Memorial admins (${admins?.length || 0})`} testid="admin-memorial-admins-list">
        {(!admins || admins.length === 0) ? <Empty>No admins added.</Empty> : (
          <Table headers={['Name', 'Email', 'Role', 'Added']}>
            {admins.map((a, idx) => (
              <tr key={a.user_id || `admin-${idx}`} className="border-t border-[hsl(var(--primary)/0.08)]">
                <td className="py-2 px-3">{a.user?.name || '—'}</td>
                <td className="py-2 px-3">
                  {a.user?.user_id ? (
                    <Link to={`/admin/accounts/${a.user.user_id}`} className="text-[hsl(var(--accent))] hover:underline">
                      {a.user.email || a.user.user_id}
                    </Link>
                  ) : (a.user?.email || '—')}
                </td>
                <td className="py-2 px-3">{a.role}</td>
                <td className="py-2 px-3">{shortDate(a.added_at)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title={`Pending invites (${invites?.length || 0})`} testid="admin-memorial-invites">
        {(!invites || invites.length === 0) ? <Empty>No outstanding invites.</Empty> : (
          <Table headers={['Email', 'Role', 'Sent', 'Accepted', 'Delivery']}>
            {invites.map((inv, idx) => (
              <tr key={inv.invite_id || `inv-${idx}`} className="border-t border-[hsl(var(--primary)/0.08)]">
                <td className="py-2 px-3">{inv.email}</td>
                <td className="py-2 px-3">{inv.role || 'admin'}</td>
                <td className="py-2 px-3">{shortDT(inv.created_at)}</td>
                <td className="py-2 px-3">{inv.accepted_at ? shortDT(inv.accepted_at) : '—'}</td>
                <td className="py-2 px-3">{inv.email_delivery_status || '—'}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </div>
  );
}

// =============================================================================
// Tributes tab
// =============================================================================
function TributesTab({ tributes, total }) {
  return (
    <div data-testid="admin-memorial-tab-tributes-content">
      <Section title={`Tributes (${total ?? tributes?.length ?? 0})`}>
        {(!tributes || tributes.length === 0) ? <Empty>No tributes received.</Empty> : (
          <Table headers={['From', 'Status', 'Created', 'Message']}>
            {tributes.map((t) => (
              <tr key={t.tribute_id || `${t.memorial_id}-${t.created_at}`} className="border-t border-[hsl(var(--primary)/0.08)]">
                <td className="py-2 px-3">{t.name || '—'}</td>
                <td className="py-2 px-3">{t.status}</td>
                <td className="py-2 px-3">{shortDate(t.created_at)}</td>
                <td className="py-2 px-3 max-w-md truncate">{t.message || t.body || '—'}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </div>
  );
}

// =============================================================================
// Shared atoms (mirroring AdminAccountDetail style for visual consistency)
// =============================================================================
function Section({ title, children, testid }) {
  return (
    <section className="mb-8" data-testid={testid}>
      <h2 className="font-serif text-2xl text-[hsl(var(--primary))] mb-3">{title}</h2>
      {children}
    </section>
  );
}
function SectionWithAction({ title, action, children, testid }) {
  return (
    <section className="mb-8" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="font-serif text-2xl text-[hsl(var(--primary))]">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}
function Grid({ children }) {
  return <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 bg-[hsl(var(--card))] border border-[hsl(var(--primary)/0.12)] rounded-2xl p-5">{children}</div>;
}
function KV({ label, value, mono }) {
  return (
    <div>
      <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium">{label}</div>
      <div className={`text-[hsl(var(--primary))] mt-1 ${mono ? 'font-mono break-all text-xs' : 'text-sm'}`}>
        {(value === 0 || value) ? value : '—'}
      </div>
    </div>
  );
}
function Table({ headers, children }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-[hsl(var(--primary)/0.12)]">
      <table className="w-full text-sm">
        <thead className="bg-[hsl(var(--primary)/0.04)] text-[hsl(var(--primary)/0.7)] uppercase tracking-[0.16em] text-[10px]">
          <tr>{headers.map((h) => <th key={h} className="text-left py-2 px-3">{h}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
function Empty({ children }) {
  return <div className="text-[hsl(var(--primary)/0.55)] text-sm bg-[hsl(var(--card))] border border-[hsl(var(--primary)/0.12)] rounded-2xl px-5 py-6">{children}</div>;
}

// =============================================================================
// Edit modals (Phase 2)
// =============================================================================
function ModalShell({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid={testid}>
      <div className="bg-[hsl(var(--card))] rounded-2xl max-w-2xl w-full p-6 my-8 border border-[hsl(var(--primary)/0.18)] shadow-xl">
        <div className="flex items-start justify-between gap-4 mb-5">
          <h3 className="font-serif text-2xl text-[hsl(var(--primary))]">{title}</h3>
          <button type="button" onClick={onClose} className="text-[hsl(var(--primary)/0.7)] hover:text-[hsl(var(--primary))] text-sm" data-testid={`${testid}-close`}>
            Close ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Labelled({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium mb-1">{label}</div>
      {children}
    </label>
  );
}

const inputCls =
  'w-full text-sm bg-transparent border-b border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none px-1 py-1.5';

function EditMemorialModal({ memorial, memorialId, onClose, onSaved }) {
  const [form, setForm] = useState({
    full_name:        memorial.full_name        || '',
    date_of_birth:    memorial.date_of_birth    || '',
    date_of_passing:  memorial.date_of_passing  || '',
    hometown:         memorial.hometown         || '',
    birthplace:       memorial.birthplace       || '',
    occupation:       memorial.occupation       || '',
    family_role:      memorial.family_role      || '',
    hero_tagline:     memorial.hero_tagline     || '',
    plan_status:      memorial.plan_status      || 'active',
    published:        memorial.published !== false,
    ai_enabled:       !!memorial.ai_enabled,
    qr_access_mode:   memorial.qr_access_mode   || 'public',
    qr_pin:           '',
    cemetery_name:    memorial.cemetery_name    || '',
    cemetery_address: memorial.cemetery_address || '',
    cemetery_city:    memorial.cemetery_city    || '',
    cemetery_country: memorial.cemetery_country || '',
    funeral_home_enabled: !!memorial.funeral_home_enabled,
    funeral_home_name:    memorial.funeral_home_name    || '',
    funeral_home_phone:   memorial.funeral_home_phone   || '',
    funeral_home_website: memorial.funeral_home_website || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Don't ship empty qr_pin string (clears means dedicated reset endpoint).
      const fields = { ...form };
      if (!fields.qr_pin) delete fields.qr_pin;
      const { data } = await api.patch(`/admin/memorials/${memorialId}`, { fields });
      if (data.updated) {
        toast.success(`Memorial updated · ${Object.keys(data.diff).length} field(s) changed.`);
      } else {
        toast.message('Nothing changed.');
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title="Edit memorial profile" onClose={onClose} testid="admin-memorial-edit-modal">
      <form onSubmit={handleSave} className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <Labelled label="Full name" testid="edit-mem-full_name">
            <input type="text" value={form.full_name} onChange={(e) => set('full_name', e.target.value)} className={inputCls} data-testid="edit-mem-full_name-input" />
          </Labelled>
          <Labelled label="Hero tagline" testid="edit-mem-tagline">
            <input type="text" value={form.hero_tagline} onChange={(e) => set('hero_tagline', e.target.value)} className={inputCls} data-testid="edit-mem-tagline-input" />
          </Labelled>
          <Labelled label="Date of birth" testid="edit-mem-dob">
            <input type="date" value={form.date_of_birth?.slice(0,10) || ''} onChange={(e) => set('date_of_birth', e.target.value)} className={inputCls} data-testid="edit-mem-dob-input" />
          </Labelled>
          <Labelled label="Date of passing" testid="edit-mem-dop">
            <input type="date" value={form.date_of_passing?.slice(0,10) || ''} onChange={(e) => set('date_of_passing', e.target.value)} className={inputCls} data-testid="edit-mem-dop-input" />
          </Labelled>
          <Labelled label="Hometown" testid="edit-mem-hometown">
            <input type="text" value={form.hometown} onChange={(e) => set('hometown', e.target.value)} className={inputCls} />
          </Labelled>
          <Labelled label="Birthplace" testid="edit-mem-birthplace">
            <input type="text" value={form.birthplace} onChange={(e) => set('birthplace', e.target.value)} className={inputCls} />
          </Labelled>
          <Labelled label="Occupation" testid="edit-mem-occupation">
            <input type="text" value={form.occupation} onChange={(e) => set('occupation', e.target.value)} className={inputCls} />
          </Labelled>
          <Labelled label="Family role" testid="edit-mem-family_role">
            <input type="text" value={form.family_role} onChange={(e) => set('family_role', e.target.value)} className={inputCls} />
          </Labelled>
          <Labelled label="Plan status" testid="edit-mem-plan_status">
            <select value={form.plan_status} onChange={(e) => set('plan_status', e.target.value)} className={inputCls} data-testid="edit-mem-plan_status-select">
              <option value="active">Active</option>
              <option value="unpaid">Unpaid</option>
              <option value="paused">Paused</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </Labelled>
          <Labelled label="QR access mode" testid="edit-mem-qr_access_mode">
            <select value={form.qr_access_mode} onChange={(e) => set('qr_access_mode', e.target.value)} className={inputCls} data-testid="edit-mem-qr_access_mode-select">
              <option value="public">Public</option>
              <option value="private">Private (PIN required)</option>
              <option value="family">Family (login required)</option>
            </select>
          </Labelled>
          <Labelled label="QR PIN (set or change — 4–8 digits)" testid="edit-mem-qr_pin">
            <input type="text" inputMode="numeric" pattern="\d{4,8}" maxLength={8} value={form.qr_pin} onChange={(e) => set('qr_pin', e.target.value.replace(/\D/g, ''))} className={inputCls} placeholder="Leave blank to keep current PIN" data-testid="edit-mem-qr_pin-input" />
          </Labelled>
          <Labelled label="Cemetery name" testid="edit-mem-cem-name">
            <input type="text" value={form.cemetery_name} onChange={(e) => set('cemetery_name', e.target.value)} className={inputCls} />
          </Labelled>
          <Labelled label="Cemetery city" testid="edit-mem-cem-city">
            <input type="text" value={form.cemetery_city} onChange={(e) => set('cemetery_city', e.target.value)} className={inputCls} />
          </Labelled>
          <Labelled label="Cemetery country" testid="edit-mem-cem-country">
            <input type="text" value={form.cemetery_country} onChange={(e) => set('cemetery_country', e.target.value)} className={inputCls} />
          </Labelled>
          <Labelled label="Cemetery address" testid="edit-mem-cem-address">
            <input type="text" value={form.cemetery_address} onChange={(e) => set('cemetery_address', e.target.value)} className={inputCls} />
          </Labelled>
          <Labelled label="Funeral home name" testid="edit-mem-fh-name">
            <input type="text" value={form.funeral_home_name} onChange={(e) => set('funeral_home_name', e.target.value)} className={inputCls} />
          </Labelled>
          <Labelled label="Funeral home phone" testid="edit-mem-fh-phone">
            <input type="text" value={form.funeral_home_phone} onChange={(e) => set('funeral_home_phone', e.target.value)} className={inputCls} />
          </Labelled>
          <Labelled label="Funeral home website" testid="edit-mem-fh-website">
            <input type="text" value={form.funeral_home_website} onChange={(e) => set('funeral_home_website', e.target.value)} className={inputCls} />
          </Labelled>
        </div>

        <div className="flex flex-wrap gap-5">
          <label className="flex items-center gap-2 text-sm text-[hsl(var(--primary))]">
            <input type="checkbox" checked={form.published} onChange={(e) => set('published', e.target.checked)} className="accent-[hsl(var(--accent))]" data-testid="edit-mem-published" />
            Published
          </label>
          <label className="flex items-center gap-2 text-sm text-[hsl(var(--primary))]">
            <input type="checkbox" checked={form.ai_enabled} onChange={(e) => set('ai_enabled', e.target.checked)} className="accent-[hsl(var(--accent))]" data-testid="edit-mem-ai_enabled" />
            AI memories enabled
          </label>
          <label className="flex items-center gap-2 text-sm text-[hsl(var(--primary))]">
            <input type="checkbox" checked={form.funeral_home_enabled} onChange={(e) => set('funeral_home_enabled', e.target.checked)} className="accent-[hsl(var(--accent))]" data-testid="edit-mem-fh-enabled" />
            Funeral home enabled
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-3 border-t border-[hsl(var(--primary)/0.08)]">
          <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full border border-[hsl(var(--primary)/0.18)] text-sm" data-testid="edit-mem-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--accent))] text-sm font-medium hover:opacity-90 transition disabled:opacity-60" data-testid="edit-mem-save">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function EditOrderModal({ kind, row, onClose, onSaved }) {
  const isPlaque    = kind === 'plaque';
  const isQr        = kind === 'qr';
  const isEngraving = kind === 'engraving';

  const endpoint = isPlaque
    ? `/admin/plaque-orders/${row.order_id}`
    : isQr
    ? `/admin/qr-orders/${row.order_id}`
    : `/admin/engraving-quotes/${row.request_id}`;

  const statusOptions = isPlaque
    ? ['queued', 'pending', 'in_production', 'shipped', 'delivered', 'cancelled', 'refunded']
    : isQr
    ? ['queued', 'pending', 'in_production', 'shipped', 'delivered', 'cancelled']
    : ['pending', 'in_review', 'quoted', 'accepted', 'declined', 'cancelled'];

  const [form, setForm] = useState({
    status:          row.status          || statusOptions[0],
    tracking_number: row.tracking_number || '',
    carrier:         row.carrier         || '',
    shipped_at:      (row.shipped_at || '').slice(0,10),
    delivered_at:    (row.delivered_at || '').slice(0,10),
    admin_notes:     row.admin_notes     || '',
    quote_amount:    row.quote_amount != null ? String(row.quote_amount) : '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fields = {};
      if (isPlaque || isQr) {
        fields.status = form.status;
        fields.tracking_number = form.tracking_number || null;
        fields.carrier = form.carrier || null;
        if (form.shipped_at)   fields.shipped_at   = `${form.shipped_at}T00:00:00+00:00`;
        if (form.delivered_at) fields.delivered_at = `${form.delivered_at}T00:00:00+00:00`;
        fields.admin_notes = form.admin_notes || null;
      } else {
        fields.status = form.status;
        if (form.quote_amount !== '') fields.quote_amount = Number(form.quote_amount);
        else fields.quote_amount = null;
        fields.admin_notes = form.admin_notes || null;
      }
      const { data } = await api.patch(endpoint, { fields });
      if (data.updated) {
        toast.success(`${isPlaque ? 'Plaque' : isQr ? 'QR' : 'Engraving'} order updated · ${Object.keys(data.diff).length} field(s) changed.`);
      } else {
        toast.message('Nothing changed.');
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const title = isPlaque ? 'Edit plaque order' : isQr ? 'Edit QR order' : 'Edit engraving quote';

  return (
    <ModalShell title={title} onClose={onClose} testid={`admin-memorial-edit-${kind}-modal`}>
      <form onSubmit={handleSave} className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <Labelled label="Status" testid={`edit-${kind}-status`}>
            <select value={form.status} onChange={(e) => set('status', e.target.value)} className={inputCls} data-testid={`edit-${kind}-status-select`}>
              {statusOptions.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </Labelled>
          {(isPlaque || isQr) && (
            <>
              <Labelled label="Carrier (e.g. UPS, FedEx, USPS)" testid={`edit-${kind}-carrier`}>
                <input type="text" value={form.carrier} onChange={(e) => set('carrier', e.target.value)} className={inputCls} data-testid={`edit-${kind}-carrier-input`} />
              </Labelled>
              <Labelled label="Tracking number" testid={`edit-${kind}-tracking`}>
                <input type="text" value={form.tracking_number} onChange={(e) => set('tracking_number', e.target.value)} className={inputCls} data-testid={`edit-${kind}-tracking-input`} />
              </Labelled>
              <Labelled label="Shipped at" testid={`edit-${kind}-shipped`}>
                <input type="date" value={form.shipped_at} onChange={(e) => set('shipped_at', e.target.value)} className={inputCls} data-testid={`edit-${kind}-shipped-input`} />
              </Labelled>
              <Labelled label="Delivered at" testid={`edit-${kind}-delivered`}>
                <input type="date" value={form.delivered_at} onChange={(e) => set('delivered_at', e.target.value)} className={inputCls} data-testid={`edit-${kind}-delivered-input`} />
              </Labelled>
            </>
          )}
          {isEngraving && (
            <Labelled label="Quote amount (USD)" testid="edit-engraving-quote-amount">
              <input type="number" step="0.01" value={form.quote_amount} onChange={(e) => set('quote_amount', e.target.value)} className={inputCls} data-testid="edit-engraving-quote-input" />
            </Labelled>
          )}
        </div>
        <Labelled label="Admin notes (internal only)" testid={`edit-${kind}-notes`}>
          <textarea rows={3} value={form.admin_notes} onChange={(e) => set('admin_notes', e.target.value)} className="w-full text-sm bg-transparent border border-[hsl(var(--primary)/0.18)] rounded-md focus:border-[hsl(var(--accent))] focus:outline-none px-2 py-1.5" data-testid={`edit-${kind}-notes-input`} />
        </Labelled>
        <div className="flex justify-end gap-3 pt-3 border-t border-[hsl(var(--primary)/0.08)]">
          <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full border border-[hsl(var(--primary)/0.18)] text-sm" data-testid={`edit-${kind}-cancel`}>Cancel</button>
          <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--accent))] text-sm font-medium hover:opacity-90 transition disabled:opacity-60" data-testid={`edit-${kind}-save`}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
